import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { createSlug } from '../../utils/helpers';
import { getLandingPageServiceListingStyle } from '../utils/landingPageTheme';
import StorefrontServiceSection from '../components/storefront/StorefrontServiceSection';
import StorefrontGuestOrLoginBanner from '../components/storefront/StorefrontGuestOrLoginBanner';
import { normalizeLandingPageSettings } from '../utils/custom-pages/pageConfig';
import { useStorefrontAuth } from '../auth/context/StorefrontAuthContext';
import { fetchStorefrontServices } from '../storefront-booking/storefrontServicesCache';
import { PageSkeleton } from '../../components/loading/skeletons';

const readGuestBrowseUnlocked = (slug) => {
  if (!slug || typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(`storefrontGuestBrowse:${slug}`) === '1';
  } catch {
    return false;
  }
};

const LandingPage = () => {
  const { companyUrl } = useParams();
  const navigate = useNavigate();
  const { companyData } = useOutletContext();
  const { storefrontClient, hasPortalAccess, loading: authLoading } = useStorefrontAuth();
  const isSignedInClient = Boolean(storefrontClient && hasPortalAccess);
  const [guestBrowseUnlocked, setGuestBrowseUnlocked] = useState(() => readGuestBrowseUnlocked(companyUrl));
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        setError(null);
        const servicesData = await fetchStorefrontServices(companyData.id);
        setServices(servicesData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (companyData?.id) {
      fetchServices();
    }
  }, [companyData?.id]);

  useEffect(() => {
    setGuestBrowseUnlocked(readGuestBrowseUnlocked(companyUrl));
  }, [companyUrl]);

  const handleServiceClick = (service) => {
    // Navigate to service details page using slug
    const serviceSlug = createSlug(service.name);
    navigate(`/book/${companyUrl}/${serviceSlug}`);
  };

  const normalizedSettings = normalizeLandingPageSettings(companyData?.landingPageSettings, companyData);
  const serviceListingStyle = getLandingPageServiceListingStyle(normalizedSettings);

  const showServiceListing = isSignedInClient || guestBrowseUnlocked;

  const handleContinueAsGuest = useCallback(() => {
    if (companyUrl) {
      try {
        window.sessionStorage.setItem(`storefrontGuestBrowse:${companyUrl}`, '1');
      } catch {
        // ignore quota / private mode
      }
    }
    setGuestBrowseUnlocked(true);
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
  }, [companyUrl]);

  if (loading) {
    return (
      <div className="landing-page">
        <PageSkeleton padded variant="cards" label="Loading storefront" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="landing-page">
        <div className="error-container">
          <h2>Oops! Something went wrong</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {!authLoading && !isSignedInClient && !guestBrowseUnlocked ? (
        <StorefrontGuestOrLoginBanner
          companyUrl={companyUrl}
          onContinueGuest={handleContinueAsGuest}
          layout="gate"
        />
      ) : null}
      {showServiceListing ? (
        <StorefrontServiceSection
          services={services}
          serviceListingStyle={serviceListingStyle}
          onServiceClick={handleServiceClick}
        />
      ) : null}
    </>
  );
};

export default LandingPage;

